import { useParams, Link } from "react-router-dom";
import { ArrowRight, BellRing, FileText, ShieldCheck } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Motion";
import NotFound from "@/pages/NotFound";
import Seo from "@/components/Seo";
import Faq from "@/components/Faq";
import EditorialSources from "@/components/EditorialSources";
import PracticalGuideLinks from "@/components/PracticalGuideLinks";
import { TRADE_DECISIONS } from "@/data/trade_decisions";
import { TRADE_COMPARISON } from "@/data/trade_comparison";
import { TRADES, tradeLabel } from "@/data/content";
import { TARIFS, TARIFS_YEAR, formatRange, formatRangeCompact, formatMonthlyRange } from "@/data/tarifs";
import { COMPANY } from "@/data/company";
import { EXPERTS_1 } from "@/data/metiers_experts_1";
import { EXPERTS_2 } from "@/data/metiers_experts_2";
import { EXPERTS_3 } from "@/data/metiers_experts_3";
import { trackEvent, openCallbackModal } from "@/lib/analytics";
import { hy } from "@/lib/hyphenate";

const EXPERTS = { ...EXPERTS_1, ...EXPERTS_2, ...EXPERTS_3 };
const SITE = COMPANY.url;
const TRADE_GUIDES = ["mention-decennale-devis-factures", "assurance-decennale-refusee-bct", "decennale-creation-entreprise"];

const SINISTRE_NUANCE =
  "Ces exemples de sinistres sont pédagogiques : ils illustrent des situations susceptibles d'engager la responsabilité décennale lorsque les conditions légales sont réunies — notamment lorsque le désordre compromet la solidité de l'ouvrage ou le rend impropre à sa destination. Ils ne constituent jamais une promesse de prise en charge.";

const TARIF_DISCLAIMER =
  "Ces montants sont donnés à titre indicatif. La prime réelle dépend notamment du chiffre d'affaires, de l'expérience, des activités effectivement déclarées, des antécédents d'assurance et de sinistres, des franchises, de l'effectif, de la zone d'intervention et des critères propres à l'assureur.";

const DOCUMENTS_LIST = [
  "KBIS ou avis de situation SIRENE",
  "Descriptif précis de vos activités déclarées",
  "Chiffre d'affaires annuel réalisé ou prévisionnel",
  "Nombre de salariés",
  "Diplômes, certifications et expérience du dirigeant",
  "Relevé d'informations et historique de sinistres si vous avez déjà été assuré",
];

const DevisCta = ({ name, label }) => (
  <div className="my-12 flex flex-wrap items-center gap-4 border-2 border-ink bg-white p-6 sm:p-8 shadow-brutal">
    <p className="font-display text-xl sm:text-2xl font-extrabold uppercase tracking-tighter flex-1 min-w-[220px]">
      Devis décennale {label} — gratuit, sans engagement.
    </p>
    <Link
      to="/devis"
      state={{ activite: name }}
      onClick={() => trackEvent("trade_page_cta", { metier: name })}
      data-testid="metier-cta-devis"
      className="inline-flex items-center gap-3 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-ink"
    >
      Obtenir mon devis décennale
      <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

const Section = ({ n, id, title, children }) => (
  <Reveal>
    <section id={id || `section-${n}`} className="scroll-mt-28 grid gap-4 border-t-2 border-ink py-10 sm:grid-cols-12 sm:gap-8 items-start">
      <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-accent sm:col-span-2">{n}</p>
      <div className="sm:col-span-10">
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-4">{hy(title)}</h2>
        {children}
      </div>
    </section>
  </Reveal>
);

export default function MetierPage() {
  const { slug } = useParams();
  const trade = TRADES.find((t) => t.slug === slug);
  const expert = EXPERTS[slug];
  const decision = TRADE_DECISIONS[slug];
  const comparison = TRADE_COMPARISON[slug];

  if (!trade || !expert) {
    return <NotFound />;
  }

  const related = expert.related
    .map((s) => TRADES.find((t) => t.slug === s))
    .filter(Boolean);
  const searchLabel = tradeLabel(trade);
  const range = TARIFS[slug];
  const title = `Assurance décennale ${trade.short || searchLabel} : prix ${formatRangeCompact(range)}/an (${TARIFS_YEAR})`;
  const description = `Décennale ${searchLabel} : ${formatRange(range)} par an (≈ ${formatMonthlyRange(range)} par mois) selon le cabinet. Travaux couverts, conseils pour payer moins cher, documents. Devis gratuit.`;
  const faq = [
    ...(comparison ? [
      {
        q: `Comment trouver une décennale ${searchLabel} pas chère ?`,
        a: `${comparison.savingAdvice} Vérifiez aussi les frais et le montant des franchises avant de retenir une offre. Aucun tarif minimum n'est garanti.`,
      },
      {
        q: `Quelle est la meilleure assurance décennale ${searchLabel} ?`,
        a: `Il n'existe pas de meilleur contrat pour toutes les entreprises. Comparez notamment : ${comparison.checks.map((item) => item.label.toLowerCase()).join(" ; ")}. Faites confirmer les activités et techniques acceptées par écrit, puis comparez le coût et les franchises à garanties équivalentes.`,
      },
    ] : []),
    ...expert.faq,
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Métiers", item: `${SITE}/metiers` },
        { "@type": "ListItem", position: 3, name: trade.name, item: `${SITE}/metiers/${slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: `Assurance décennale ${trade.name}`,
      name: `Assurance RC Pro et décennale ${searchLabel}`,
      provider: { "@id": `${SITE}/#organization` },
      areaServed: "FR",
      audience: { "@type": "BusinessAudience", name: `Entreprises et artisans : ${searchLabel}` },
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="metier-page">
      <Seo title={title} description={description} path={`/metiers/${slug}`} jsonLd={jsonLd} />
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-14 lg:py-20">
        <Reveal y={10}>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-6">
            <Link to="/" className="hover:text-accent">Accueil</Link>
            <span className="mx-2 text-accent">/</span>
            <Link to="/metiers" className="hover:text-accent">Métiers</Link>
            <span className="mx-2 text-accent">/</span>
            <span className="text-ink">{trade.name}</span>
          </p>
          <SectionTag className="mb-4">Code civil, art. 1792 et suivants — Code des assurances, art. L241-1</SectionTag>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tighter leading-[0.95] mb-6 max-w-5xl">
            {hy("Assurance décennale")} <span className="text-accent">{hy(trade.name)}</span> : prix, {hy("garanties")} et devis
          </h1>
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-ink/75 mb-10">{expert.intro}</p>
          <div className="flex flex-wrap gap-4 mb-8">
            <Link
              to="/devis"
              state={{ activite: trade.name }}
              onClick={() => trackEvent("trade_page_cta", { metier: trade.name })}
              data-testid="metier-cta-devis-top"
              className="inline-flex items-center gap-3 border-2 border-ink bg-accent px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper shadow-brutal transition-all hover:bg-ink hover:shadow-none"
            >
              Obtenir mon devis décennale
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              onClick={openCallbackModal}
              data-testid="metier-cta-callback"
              className="inline-flex items-center gap-2 border-2 border-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-ink hover:text-paper"
            >
              <BellRing className="h-4 w-4" />
              Être rappelé
            </button>
          </div>
          <div className="mb-6 flex flex-wrap gap-x-8 gap-y-2 border-y border-ink/15 py-4">
            {["22 métiers du bâtiment", "Rappel rapide par un spécialiste BTP", "Devis gratuit & sans engagement"].map((t) => (
              <p key={t} className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60 flex items-center gap-2">
                <span className="text-accent">◆</span>
                {t}
              </p>
            ))}
          </div>
        </Reveal>

        {decision && <section aria-label="Les points essentiels pour votre métier" className="my-8 border-l-4 border-accent bg-white p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold mb-4">Décennale {searchLabel} : l’essentiel avant le devis</h2>
          <p className="max-w-3xl leading-relaxed text-ink/80 mb-5">{decision.summary}</p>
          <h3 className="font-bold mb-3">Travaux à préciser à l’assureur</h3>
          <ul className="grid gap-3 sm:grid-cols-2 list-disc pl-5 text-sm leading-relaxed text-ink/80">{decision.activities.map((activity) => <li key={activity}>{activity}</li>)}</ul>
          <p className="text-sm leading-relaxed text-ink/70 mt-5">Ces points servent à décrire votre activité. Ils ne sont pas une liste de garanties acquises : contrôlez les activités et techniques acceptées dans votre contrat.</p>
        </section>}
        <nav aria-label="Sommaire de la fiche métier" className="my-8 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <a className="underline underline-offset-4 hover:text-accent" href="#section-01">Obligation</a>
          <a className="underline underline-offset-4 hover:text-accent" href="#section-02">Travaux concernés</a>
          <a className="underline underline-offset-4 hover:text-accent" href="#section-05">Prix et critères</a>
          {comparison && <a className="underline underline-offset-4 hover:text-accent" href="#decennale-pas-chere">Payer moins cher</a>}
          {comparison && <a className="underline underline-offset-4 hover:text-accent" href="#meilleure-decennale">Choisir le meilleur contrat pour vous</a>}
          <a className="underline underline-offset-4 hover:text-accent" href="#section-07">Documents pour le devis</a>
          <a className="underline underline-offset-4 hover:text-accent" href="#section-08">Entreprise en création</a>
        </nav>

        <Section n="01" title={`L'assurance décennale est-elle obligatoire pour un ${searchLabel} ?`}>
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl">{expert.obligatoire}</p>
        </Section>

        <Section n="02" title={`Quels travaux peuvent engager la responsabilité décennale d'un ${searchLabel} ?`}>
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl">{expert.travaux}</p>
        </Section>

        <Section n="03" title="Exemples d'ouvrages susceptibles de relever de la décennale">
          <ul className="grid gap-3 sm:grid-cols-2 max-w-3xl">
            {expert.ouvrages.map((o, i) => (
              <li key={i} className="flex gap-3 border border-ink/15 bg-white p-4 text-sm leading-relaxed text-ink/80">
                <ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                {o}
              </li>
            ))}
          </ul>
        </Section>

        <Section n="04" title="Exemples de sinistres rencontrés dans le métier">
          <ul className="space-y-3 max-w-3xl">
            {expert.sinistres.map((s, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed text-ink/75">
                <span className="font-mono text-[10px] text-accent pt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                {s}
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-3xl border border-ink/15 bg-smoke/40 p-4 text-xs leading-relaxed text-ink/65">
            {SINISTRE_NUANCE}
          </p>
        </Section>

        <DevisCta name={trade.name} label={searchLabel} />

        <Section n="05" title={`Combien coûte une assurance décennale ${searchLabel} ?`}>
          <div className="mb-6 inline-block border-2 border-ink bg-night px-8 py-6 text-paper shadow-brutal-accent">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 mb-2">
              Repère indicatif communiqué par le cabinet (prime annuelle)
            </p>
            <p className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-accent" data-testid="metier-tarif">
              {formatRange(range)} <span className="text-lg text-paper/70">/ an</span>
            </p>
            <p className="mt-2 font-display text-lg font-bold tracking-tight" data-testid="metier-tarif-mensuel">
              soit environ {formatMonthlyRange(range)} <span className="text-sm text-paper/70">/ mois</span>
            </p>
          </div>
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl mb-4">{expert.cout}</p>
          <p className="max-w-3xl border border-ink/15 bg-smoke/40 p-4 text-xs leading-relaxed text-ink/65">
            {TARIF_DISCLAIMER}
          </p>
        </Section>

        <Section n="06" title="Pourquoi le tarif varie-t-il ?">
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl">{expert.tarifVarie}</p>
          <p className="mt-4 text-sm text-ink/70 max-w-3xl">
            Le détail des critères de calcul et les fourchettes des 22 métiers sont sur notre page{" "}
            <Link to="/prix" className="underline underline-offset-4 hover:text-accent" data-testid="metier-link-prix">
              prix assurance décennale
            </Link>
            .
          </p>
        </Section>

        {comparison && <>
          <Section n="07" id="decennale-pas-chere" title={`Décennale ${searchLabel} pas chère : comment réduire le coût ?`}>
            <p className="text-base leading-relaxed text-ink/75 max-w-3xl mb-5">{comparison.savingAdvice}</p>
            <ul className="list-disc pl-5 space-y-3 text-sm leading-relaxed text-ink/80 max-w-3xl">
              <li>Demandez le montant annuel taxes comprises, les éventuels frais de dossier ou de courtage et le coût du paiement fractionné. Vérifiez ce qui est déjà inclus pour ne pas compter deux fois les mêmes frais.</li>
              <li>Faites chiffrer plusieurs niveaux de franchise si l’assureur le permet. Une baisse de cotisation peut s’accompagner d’un montant plus élevé à supporter en cas de sinistre.</li>
              <li>Présentez vos justificatifs d’expérience et un chiffre d’affaires cohérent avec vos chantiers. Conservez toutes vos activités réelles dans le dossier ; demandez comment la prime peut être régularisée.</li>
            </ul>
            <div className="mt-6 border-l-4 border-accent bg-white p-5 max-w-3xl">
              <h3 className="font-bold mb-2">L’économie à vérifier avant de signer</h3>
              <p className="text-sm leading-relaxed text-ink/80">{comparison.pitfall}</p>
            </div>
            <p className="mt-5 text-sm text-ink/70 max-w-3xl">Un paiement mensuel facilite l’étalement du budget, mais ne prouve pas que le contrat coûte moins cher sur l’année. Une franchise n’est pas une cotisation annuelle : comparez séparément le budget de l’assurance et le risque restant à votre charge.</p>
          </Section>
          <Section n="08" id="meilleure-decennale" title={`Meilleure assurance décennale ${searchLabel} : quels critères comparer ?`}>
            <p className="text-base leading-relaxed text-ink/75 max-w-3xl mb-6">Le meilleur contrat dépend de vos travaux, des conditions de garantie et de votre budget. Commencez par faire confirmer les activités et techniques acceptées ; départagez ensuite les offres sur leur coût et leurs franchises. Voici trois points propres à votre métier.</p>
            <dl className="grid gap-4 max-w-3xl">
              {comparison.checks.map((item) => <div key={item.label} className="border border-ink/15 bg-white p-5">
                <dt className="font-bold mb-2">{item.label}</dt>
                <dd className="text-sm leading-relaxed text-ink/75">{item.detail}</dd>
              </div>)}
            </dl>
            <p className="mt-6 text-sm leading-relaxed text-ink/75 max-w-3xl">Contrôlez également la date d’effet, la période et la zone couvertes, les exclusions, les modalités de déclaration des sinistres et l’identité de l’assureur. Pour relire les documents, consultez notre <Link to="/guide/verifier-attestation-decennale" className="underline underline-offset-4 hover:text-accent">guide de vérification de l’attestation décennale</Link>.</p>
            <p className="mt-4 text-sm leading-relaxed text-ink/70 max-w-3xl">Ces critères aident à comparer des propositions. Ils ne constituent ni un classement d’assureurs ni une promesse d’obtenir le tarif le plus bas du marché.</p>
            <p className="mt-4 text-xs leading-relaxed text-ink/60 max-w-3xl">Pour la méthode de comparaison des prix et garanties : <a href="https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/assurance-les-details-connaitre-avant-de-sengager" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-accent">conseils de la DGCCRF avant de souscrire une assurance</a>.</p>
            <DevisCta name={trade.name} label={searchLabel} />
          </Section>
        </>}

        <Section n="09" id="section-07" title="Quels documents préparer pour obtenir un devis ?">
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl mb-4">{expert.documents}</p>
          <ul className="grid gap-3 sm:grid-cols-2 max-w-3xl">
            {DOCUMENTS_LIST.map((d, i) => (
              <li key={i} className="flex gap-3 border border-ink/15 bg-white p-4 text-sm leading-relaxed text-ink/80">
                <FileText className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                {d}
              </li>
            ))}
          </ul>
        </Section>

        <Section n="10" id="section-08" title="Création d'entreprise, reprise d'activité, absence d'assurance antérieure">
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl">{expert.creation}</p>
        </Section>

        <DevisCta name={trade.name} label={searchLabel} />

        <Section n="11" id="section-09" title={`RC Pro et décennale : quelle différence pour un ${searchLabel} ?`}>
          <p className="text-base leading-relaxed text-ink/75 max-w-3xl">{expert.rcpro}</p>
        </Section>

        <Reveal>
          <section className="border-t-2 border-ink py-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tighter mb-8">
              FAQ assurance décennale {searchLabel}
            </h2>
            <Faq items={faq} prefix={`metier-faq-${slug}`} />
          </section>
        </Reveal>

        <Reveal className="mb-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-6">Pour aller plus loin</p>
          <div className="grid grid-cols-2 lg:grid-cols-3 border-t border-l border-ink/15 mb-8">
            <Link to="/assurance-decennale" data-testid="metier-link-decennale" className="border-b border-r border-ink/15 p-5 font-display text-sm font-bold uppercase tracking-tight transition-colors hover:bg-accent hover:text-paper">
              Assurance décennale : le guide
            </Link>
            <Link to="/prix" data-testid="metier-link-prix-2" className="border-b border-r border-ink/15 p-5 font-display text-sm font-bold uppercase tracking-tight transition-colors hover:bg-accent hover:text-paper">
              Prix par métier (2026)
            </Link>
            <Link to="/devis-assurance-decennale" data-testid="metier-link-devis-guide" className="border-b border-r border-ink/15 p-5 font-display text-sm font-bold uppercase tracking-tight transition-colors hover:bg-accent hover:text-paper">
              Obtenir un devis : la démarche
            </Link>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink/60 mb-6">Métiers connexes</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-ink/15">
            {related.map((t) => (
              <Link
                key={t.slug}
                to={`/metiers/${t.slug}`}
                data-testid={`metier-related-${t.slug}`}
                className="border-b border-r border-ink/15 p-6 font-display text-base font-bold uppercase tracking-tight transition-colors hover:bg-ink hover:text-paper"
              >
                {t.name}
              </Link>
            ))}
          </div>
        </Reveal>
        <EditorialSources updated="2026-09-24" />
        <PracticalGuideLinks slugs={TRADE_GUIDES} title={`Guides utiles pour votre entreprise`} />
      </div>

      <section className="bg-accent text-paper">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 py-16">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tighter leading-[0.95]">
              {hy(`Demandez votre devis décennale ${searchLabel}.`)}
            </h2>
            <Link
              to="/devis"
              state={{ activite: trade.name }}
              onClick={() => trackEvent("trade_page_cta", { metier: trade.name })}
              data-testid="metier-band-cta"
              className="mt-8 inline-flex items-center gap-3 border-2 border-night bg-night px-10 py-5 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night"
            >
              Obtenir mon devis décennale
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
